import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import * as dat from "lil-gui";
import testVertexShader from "./shaders/test/vertex.glsl";
import testFragmentShader from "./shaders/test/fragment.glsl";

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

// Geometry
const geometry = new THREE.PlaneGeometry(1, 1, 256, 256);

const textureLoader = new THREE.TextureLoader();
const depthMapTexture = textureLoader.load("depth.png");
const forrestTexture = textureLoader.load("forrest.png", () => {
  let depthScale = 0.9;
  const material = new THREE.ShaderMaterial({
    vertexShader: `
    uniform sampler2D map;
    uniform float depthScale;
    varying vec2 vUv;
    varying float vDepth;
    
    void main() {
      vUv = uv;
      
      // Pass the depth value to the fragment shader
      float depth = texture2D(map, vUv).r;
      vDepth = depth;

      // Calculate the adjusted Z position using the depth value
      float z = depth * depthScale - 1.0;
      
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position.xy, z, 1.0);
    }
  `,
    fragmentShader: `
  uniform sampler2D myTexture;
  varying vec2 vUv;
  varying float vDepth;
  
  void main() {
    vec4 texColor = texture2D(myTexture, vUv);

    texColor.rgb *= vDepth * 1.3;

    gl_FragColor = texColor;
  }
`,
    uniforms: {
      map: { value: depthMapTexture },
      myTexture: { value: forrestTexture },
      depthScale: { value: depthScale },
    },
    //   wireframe: true,
  });

  document.addEventListener("wheel", (event) => {
    // if event.deltaY is positive, increase depthScale, else decrease depthScale
    if (event.deltaY > 0) {
      depthScale += 0.1;
      camera.position.z += 0.1;
    } else {
      depthScale -= 0.1;
      camera.position.z -= 0.1;
    }

    material.uniforms.depthScale.value = depthScale;
  });

  document.addEventListener("contextmenu", () => {
    material.uniforms.depthScale.value = 0.9;
    depthScale = 0.9;
    camera.position.z = 0.8;
  });

  // Mesh
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  /**
   * Size
   */
  const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  window.addEventListener("resize", () => {
    // Update sizes
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;

    // Update camera
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();

    // Update renderer
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  });

  // add event listener for pressing the x key and toggle on the mesh's wireframe mode
  document.addEventListener("keydown", (event) => {
    if (event.key === "x") {
      mesh.material.wireframe = !mesh.material.wireframe;
    }
  });

  let motionEnabled = false;
  const tilt = { x: 0, y: 0 };

  const motionPermissionKey = "motionPermissionGranted";

  // Check if motion permission has been previously granted
  const isMotionPermissionGranted =
    localStorage.getItem(motionPermissionKey) === "true";

  // Check if the device supports motion controls
  if (typeof DeviceOrientationEvent.requestPermission === "function") {
    if (isMotionPermissionGranted) {
      enableMotionControls();
    } else {
      // Add a button to request permission
      const permissionButton = document.createElement("button");
      permissionButton.textContent = "Request Motion Permission";
      permissionButton.style.position = "absolute";
      permissionButton.style.top = "50%";
      permissionButton.style.left = "50%";
      permissionButton.style.transform = "translate(-50%, -50%)";
      permissionButton.style.fontSize = "24px";
      permissionButton.style.padding = "12px 24px";
      document.body.appendChild(permissionButton);

      // Add event listener to request permission on button click
      permissionButton.addEventListener("click", () => {
        DeviceOrientationEvent.requestPermission()
          .then((permissionState) => {
            if (permissionState === "granted") {
              // Store the motion permission status
              localStorage.setItem(motionPermissionKey, "true");
              enableMotionControls();
              permissionButton.remove();
            }
          })
          .catch(console.error);
      });
    }
  }

  // Enable motion controls and handle device orientation
  function enableMotionControls() {
    // // add an event listener for clicking which sets the mat depthScale to 1.1
    document.addEventListener("click", () => {
      depthScale += 0.1;
      material.uniforms.depthScale.value = depthScale;
      camera.position.z += 0.08;
    });

    motionEnabled = true;
    window.addEventListener("deviceorientation", handleDeviceOrientation);
  }

  // Default rotation angles
  const defaultRotationX = THREE.MathUtils.degToRad(45); // Default rotation around the x-axis in radians
  const defaultRotationY = 0; // Default rotation around the y-axis in radians

  // Set the mesh's initial rotation to face straight forward
  mesh.rotation.set(defaultRotationX, defaultRotationY, 0);

  // Handle device orientation
  function handleDeviceOrientation(event) {
    const { beta, gamma } = event;

    // Calculate adjusted tilt values by subtracting the default rotations
    const adjustedTiltX = THREE.MathUtils.degToRad(beta) - defaultRotationX;
    let adjustedTiltY = THREE.MathUtils.degToRad(gamma) - defaultRotationY;

    // Clamp the Y rotation within the desired range
    adjustedTiltY = THREE.MathUtils.clamp(
      adjustedTiltY,
      THREE.MathUtils.degToRad(-45),
      THREE.MathUtils.degToRad(45)
    );

    // Update the mesh rotation based on the adjusted tilt
    tilt.x = adjustedTiltX;
    tilt.y = adjustedTiltY;
  }

  /**
   * Camera
   */
  // Base camera
  const camera = new THREE.PerspectiveCamera(
    75,
    sizes.width / sizes.height,
    0.1,
    100
  );
  camera.position.z = 0.8;
  scene.add(camera);

  // // Controls
  // const controls = new OrbitControls(camera, canvas);
  // controls.enableDamping = true;

  const mouse = new THREE.Vector2();
  document.addEventListener("mousemove", (event) => {
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  });

  /**
   * Renderer
   */
  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
  });
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  /**
   * Animate
   */
  const tick = () => {
    // Update controls
    //   controls.update();

    if (motionEnabled) {
      mesh.rotation.x = tilt.x;
      mesh.rotation.y = tilt.y;
    } else {
      mesh.lookAt(mouse.x, mouse.y, 5);
    }

    // Render
    renderer.render(scene, camera);

    // Call tick again on the next frame
    window.requestAnimationFrame(tick);
  };

  tick();
});
